import { useAppToast } from "@/hooks/useAppToast";
import {
  Box,
  BoxProps,
  Flex,
  Heading,
  Text,
  TextProps,
  UseRadioGroupProps,
} from "@chakra-ui/react";
import { AlertMessage, Image, ImageSelectFieldProps } from "@picovico/ui";
import { LoyaltyConfig } from "custom-types";
import { capitalize } from "lodash";
import { useMemo } from "react";
import { PaymentMethodDTO } from "types/api";
import { Member } from "types/user";

function PaymentSelector({
  headingText = "Payment Method",
  headingTitleProps,
  paymentMethodCardProps,
  paymentMethodTextProps,
  selectedBorderColor,
  selectedBg,
  unselectedBorderColor,
  unselectedBg,
  amount,
  loyaltyConfig,
  member,
  disableLoyalty = false,
  data,
  ...props
}: UseRadioGroupProps &
  Omit<ImageSelectFieldProps, "images"> & {
    onSelected?: (value: PaymentMethodDTO) => void;
    loyaltyConfig: LoyaltyConfig | null;
    member?: Member;
    amount: number;
    disableLoyalty?: boolean;
    data?: PaymentMethodDTO[];
  } & {
    headingText?: string;
    headingTitleProps?: BoxProps;
    paymentMethodCardProps?: BoxProps;
    paymentMethodTextProps?: TextProps;
    selectedBorderColor?: string;
    selectedBg?: string;
    unselectedBorderColor?: string;
    unselectedBg?: string;
  }) {
  const { error: errorToast } = useAppToast();

  const memberPointsToAmount = useMemo(() => {
    if (!loyaltyConfig) return 0;
    const { point_to_amount_conversion_rate = 0 } = loyaltyConfig;
    return (
      (point_to_amount_conversion_rate / 100) * (member?.available_points || 0)
    );
  }, [loyaltyConfig, member?.available_points]);

  // Barred members lose the loyalty option outright — not merely when the balance is short.
  const isLoyaltyBarred = !!member?.is_blacklisted;
  const haveEnoughPoints = !isLoyaltyBarred && memberPointsToAmount >= amount;

  return (
    <>
      {/* {isError && (
        <AlertMessage status="error" message={error?.message || ""} />
      )} */}
      {data?.length === 0 && (
        <AlertMessage message="There's no payment methods" />
      )}

      <Heading color="surface.500" size="md" py={"1rem"} {...headingTitleProps}>
        {headingText}
      </Heading>

      <Flex flexWrap="wrap" gap={3}>
        {data?.map((method) => {
          if (
            method.payment_method === "LOYALTY_POINT" &&
            (disableLoyalty ||
              isLoyaltyBarred ||
              !haveEnoughPoints ||
              !loyaltyConfig?.enable)
          )
            return null;

          const isSelected = props.value === method.idx;
          return (
            <Box
              key={method.idx}
              as="label"
              display="flex"
              alignItems="center"
              gap={3}
              px={{ base: 2, sm: 4 }}
              py={{ base: 1, sm: 2 }}
              border="2px solid"
              borderColor={
                isSelected
                  ? (selectedBorderColor ?? "blue.500")
                  : (unselectedBorderColor ?? "gray.300")
              }
              bg={
                isSelected
                  ? (selectedBg ?? "blue.100")
                  : (unselectedBg ?? "white")
              }
              borderRadius="lg"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                borderColor: isSelected
                  ? (selectedBorderColor ?? "blue.600")
                  : (unselectedBorderColor ?? "gray.400"),
              }}
              {...paymentMethodCardProps}
            >
              <input
                type="radio"
                name="payment_method"
                value={method.idx}
                checked={isSelected}
                onChange={() => {
                  if (
                    method.payment_method === "LOYALTY_POINT" &&
                    !haveEnoughPoints
                  ) {
                    errorToast(
                      "You don't have enough points to pay with loyalty points.",
                    );
                  } else {
                    props.onSelected?.(method);
                  }
                }}
                style={{
                  position: "absolute",
                  width: "1px",
                  height: "1px",
                  padding: 0,
                  margin: "-1px",
                  overflow: "hidden",
                  clip: "rect(0,0,0,0)",
                  whiteSpace: "nowrap",
                  border: 0,
                }}
              />
              <Image
                height="24px"
                width="24px"
                src={method?.image || method?.logo}
                objectFit="contain"
                loading="lazy"
                alt={method.payment_method}
              />
              <Text
                color="gray.700"
                fontWeight="medium"
                {...paymentMethodTextProps}
              >
                {capitalize(method.payment_method?.split("_").join(" ") || "")}
              </Text>
            </Box>
          );
        })}
      </Flex>

      {/* <ImageSelectField
        loading={isLoading}
        images={
          data?.data.map((method) => ({
            image: method.image,
            title: method.payment_method,
            value: method.idx,
          })) || []
        }
        {...props}
      /> */}
    </>
  );
}

export default PaymentSelector;
